import React, { useEffect, useState } from "react";
import MainContainer from "../../../components/partials/maincontainer";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useRequestStore } from "../../../data/stores/loggerStore";
import { apiCall } from "../../../data/useFetcher";
import AboutEdit from "./components/about-form";
import DataCollectionEdit from "./components/data-collection-form";
import DataFlowEdit from "./components/data-flow-form";
import ExternalDataSharingEdit from "./components/external-data-sharing-form";
import FitForPurposeEdit from "./components/fit-for-purpose-form";
import axios from "axios";
import toast from "react-hot-toast";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

// Complete form schema with all fields
const formSchema = z.object({
  // About form fields
  erpModule: z.string().min(1, "Data system is required"),
  systemDeveloper: z.string().min(1, "System developer name is required"),
  dateInstalled: z.string().min(1, "Installation date is required"),
  annualLicenseFee: z.string().min(1, "Annual license fees are required"),
  systemPurpose: z.string().min(1, "Main purpose description is required"),
  annualLicenseFeeCurrency: z.string().min(1, "Currency is required"),

  // Data Collection form fields
  toolUsedToCollectData: z.string().min(1, "Tool name is required"),
  informationCollectionTool: z
    .string()
    .min(1, "Technology selection is required"),
  oftenCollected: z.string().min(1, "Data collection frequency is required"),
  dataCollectionToolPurposeServe: z
    .string()
    .min(1, "Tool effectiveness rating is required"),

  // Data Flow form fields
  deptsFeedDataSystem: z
    .array(z.string())
    .min(1, "Department selection is required"),
  systemsFeedDataSystem: z
    .array(z.string())
    .min(1, "System selection is required"),
  deptReceiveData: z
    .array(z.string())
    .min(1, "Receiving department selection is required"),
  deptSystemReceiveData: z
    .array(z.string())
    .min(1, "Receiving system selection is required"),
  reportProduced: z.string().min(1, "Report description is required"),

  // External Data Sharing form fields
  systemInformKPI: z.array(z.string()).min(1, "KPI selection is required"),
  systemInformArea: z.array(z.string()).min(1, "Area selection is required"),
  systemReceiveData: z
    .string()
    .min(1, "External system information is required"),
  systemCommunicationEffectiveness: z
    .string()
    .min(1, "Communication effectiveness is required"),

  // Fit for Purpose form fields
  effectivenessPurposeLevel: z
    .string()
    .min(1, "Effectiveness level is required"),
  dataSecurity: z.string().min(1, "Data security selection is required"),
  supportTainingNeeds: z.string().optional(),
  improvementRequired: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

const AddDataSystem = () => {
  const { id, step } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "about";

  const { dataSystemAll, getDynamicLogger }: any = useRequestStore();

  const [datum, setDatum] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  // Tab order for navigation
  const tabOrder = [
    "about",
    "data-collection",
    "data-flow",
    "external-data",
    "fit-purpose",
  ];

  // Check if current tab is the last one
  const isLastTab = activeTab === tabOrder[tabOrder.length - 1];

  // Initialize react-hook-form with zod validation
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      // About form fields
      erpModule: "",
      systemDeveloper: "",
      dateInstalled: "",
      annualLicenseFee: "",
      systemPurpose: "",
      annualLicenseFeeCurrency: "",

      // Data Collection form fields
      toolUsedToCollectData: "",
      informationCollectionTool: "",
      oftenCollected: "",
      dataCollectionToolPurposeServe: "",

      // Data Flow form fields
      deptsFeedDataSystem: [],
      systemsFeedDataSystem: [],
      deptReceiveData: [],
      deptSystemReceiveData: [],
      reportProduced: "",

      // External Data Sharing form fields
      systemInformKPI: [],
      systemInformArea: [],
      systemReceiveData: "",
      systemCommunicationEffectiveness: "",

      // Fit for Purpose form fields
      effectivenessPurposeLevel: "",
      dataSecurity: "",
      supportTainingNeeds: "",
      improvementRequired: "",
    },
    mode: "onChange",
  });

  // Get current tab fields for validation
  const getTabFields = (tabId: string): (keyof FormData)[] => {
    switch (tabId) {
      case "about":
        return [
          "erpModule",
          "systemDeveloper",
          "dateInstalled",
          "annualLicenseFee",
          "systemPurpose",
          "annualLicenseFeeCurrency",
        ];
      case "data-collection":
        return [
          "toolUsedToCollectData",
          "informationCollectionTool",
          "oftenCollected",
          "dataCollectionToolPurposeServe",
        ];
      case "data-flow":
        return [
          "deptsFeedDataSystem",
          "systemsFeedDataSystem",
          "deptReceiveData",
          "deptSystemReceiveData",
          "reportProduced",
        ];
      case "external-data":
        return [
          "systemInformKPI",
          "systemInformArea",
          "systemReceiveData",
          "systemCommunicationEffectiveness",
        ];
      case "fit-purpose":
        return ["effectivenessPurposeLevel", "dataSecurity"];
      default:
        return [];
    }
  };

  // Handle tab navigation without validation (for clicking tabs directly)
  const handleTabClick = async (tabId: string) => {
    // Allow free navigation between tabs - data is retained in form state
    setSearchParams({ tab: tabId });
  };

  // Handle Next button click
  const handleNext = async () => {
    // Validate only current tab fields
    const currentTabFields = getTabFields(activeTab);
    const isCurrentTabValid = await trigger(currentTabFields);

    if (isCurrentTabValid) {
      const currentTabIndex = tabOrder.indexOf(activeTab);
      const nextTab = tabOrder[currentTabIndex + 1];
      setSearchParams({ tab: nextTab });
    } else {
      toast.error("Please complete all required fields before proceeding");
    }
  };

  // Handle form submission for the last tab
  const handleFinalSubmit = async (formData: FormData) => {
    setIsSubmitting(true);
    try {
      const payload = {
        datasystems: [{ ...formData }],
        email: datum?.profile?.user?.email,
        user: datum?.profile?.user?._id,
      };

      const res = await axios.put(`/api/v1/data-system`, payload);
      toast.success(res?.data?.message ?? "Data system created successfully!");
      navigate("/invitation");
    } catch (error: any) {
      console.error("Submission error:", error);
      if (error?.response?.data?.error) {
        error.response.data.error.forEach((err: any) =>
          toast.error(err.message || err.msg)
        );
      } else {
        toast.error(error?.response?.data?.message || "Something went wrong");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get button text based on current tab
  const getButtonText = () => {
    if (isSubmitting) {
      return isLastTab ? "Creating Data System..." : "Processing...";
    }
    return isLastTab ? "Submit All Data" : "Next";
  };

  // Fetch ERP modules on mount
  useEffect(() => {
    dataSystemAll?.docs?.map(
      (it: any) => it?.profile?._id === step && setDatum(it)
    );
  }, [id, dataSystemAll, step]);
  console.log(datum, "datum");
  useEffect(() => {
    if (!datum) return;
    apiCall({
      type: "get",
      url: `/api/v1/data-system/manage-effectivenesspurposelevel?datasystem=${
        datum?.erpModule
      }${
        datum?.organization
          ? `&organization=${datum?.organization?._id || datum?.organization}`
          : ""
      }`,
      getter: (d: any) => getDynamicLogger(d, "effectivenessOrg"),
    });
  }, [datum]);

  // if (!datum) return null;

  const handleBack = () => {
    navigate("/invitation");
  };

  const tabs = [
    {
      id: "about",
      label: "About",
      component: (
        <AboutEdit
          control={control}
          errors={errors}
          watch={watch}
          setValue={setValue}
        />
      ),
    },
    {
      id: "data-collection",
      label: "Data Collection",
      component: (
        <DataCollectionEdit
          control={control}
          errors={errors}
          watch={watch}
          setValue={setValue}
        />
      ),
    },
    {
      id: "data-flow",
      label: "Data Flow",
      component: (
        <DataFlowEdit
          control={control}
          errors={errors}
          watch={watch}
          setValue={setValue}
          datum={datum}
        />
      ),
    },
    {
      id: "external-data",
      label: "External Data Sharing",
      component: (
        <ExternalDataSharingEdit
          control={control}
          errors={errors}
          watch={watch}
          setValue={setValue}
        />
      ),
    },
    {
      id: "fit-purpose",
      label: "Fit For Purpose",
      component: (
        <FitForPurposeEdit
          control={control}
          errors={errors}
          watch={watch}
          setValue={setValue}
        />
      ),
    },
  ];

  // Check if current tab has errors
  const currentTabFields = getTabFields(activeTab);
  const hasCurrentTabErrors = currentTabFields.some((field) => errors[field]);

  return (
    <div>
      <MainContainer>
        <div className="min-h-screen p-4">
          <div className="max-w-4xl">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <button
                className="p-2 hover:bg-gray-100 rounded-md transition-colors"
                onClick={handleBack}
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <h1 className="text-xl font-semibold text-gray-900">
                Add ERP Module
              </h1>
            </div>

            {/* Navigation Tabs */}
            <div className="mb-6">
              <div className="flex w-fit bg-[#F9FAFB] border-[#F2F4F7] border rounded-lg py-2 px-3">
                {tabs.map((tab) => {
                  const tabFields = getTabFields(tab.id);
                  const hasTabErrors = tabFields.some((field) => errors[field]);

                  return (
                    <button
                      key={tab.id}
                      onClick={() => handleTabClick(tab.id)}
                      className={`px-4 py-2 text-sm font-medium transition-colors relative ${
                        activeTab === tab.id
                          ? "bg-[white] rounded-lg text-[#344054]"
                          : "border-transparent text-[#667085] hover:text-gray-700 hover:border-gray-300"
                      }`}
                    >
                      {tab.label}
                      {hasTabErrors && (
                        <span className="absolute -top-1 -right-1 h-2 w-2 bg-red-500 rounded-full"></span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Progress indicator */}
            <div className="mb-6">
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>
                  Step {tabOrder.indexOf(activeTab) + 1} of {tabOrder.length}
                </span>
                <span>
                  {Math.round(
                    ((tabOrder.indexOf(activeTab) + 1) / tabOrder.length) * 100
                  )}
                  % Complete
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                <div
                  className="bg-[#2070B8] h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${
                      ((tabOrder.indexOf(activeTab) + 1) / tabOrder.length) *
                      100
                    }%`,
                  }}
                ></div>
              </div>
            </div>

            {/* Conditional Form/Content */}
            <form
              onSubmit={
                isLastTab
                  ? handleSubmit(handleFinalSubmit)
                  : (e) => {
                      e.preventDefault();
                      handleNext();
                    }
              }
            >
              {/* Active Tab Content */}
              <div className="mb-8">
                {tabs.find((tab) => tab.id === activeTab)?.component}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-6 border-t">
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-12 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-12 py-2.5 bg-[#2070B8] font-medium text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {getButtonText()}
                </button>
              </div>
            </form>

            {/* Help Text */}
            <div className="mt-4">
              <div className="text-sm text-gray-600">
                {isLastTab
                  ? "Review your information and click 'Submit All Data' to create the data system"
                  : "Complete the current tab and click 'Next' to continue"}
              </div>
              {hasCurrentTabErrors && (
                <div className="text-sm text-red-600 mt-1">
                  Please fix validation errors in the current tab before
                  proceeding
                </div>
              )}
            </div>
          </div>
        </div>
      </MainContainer>
    </div>
  );
};

export default AddDataSystem;
